import { defineStore } from 'pinia';
import { useForm } from '@inertiajs/vue3';
import { Notify } from 'quasar';
import axios from 'axios';

axios.defaults.baseURL = import.meta.env.AXIOS_BASE_URL;
axios.defaults.withCredentials = true;
axios.defaults.headers.common['X-Requested-With'] = 'XMLHttpRequest';

function emptyContact() {
    return {
        id: null,
        org_name: null,
        region: null,
        location: null,
        name: null,
        address: null,
        first_name: null,
        last_name: null,
        country: null,
        city: null,
        state: null,
        county: null,
        postal_code: null,
        street_address: null,
        suite: null,
        sub_suite: null,
        email: null,
        phone: null,
        mobile: null,
        fax: null,
        created_at: null,
        deleted_at: null,
    };
}

function emptyCustomer() {
    return {
        id: null,
        contact_id: null,
        profit_center: null,
        created_at: null,
        updated_at: null,
        last_worked_at: null,
        deleted_at: null,
        issues: {
            needs_workers_assigned: false,
            needs_rate_tables_created: false,
        },
        has_settings: false,
        contact: emptyContact(),
        workTypes: [],
        subCustomers: [],
        identifier_types: [],
        alt_contact: [],
    };
}

function emptyWorkItem(workOrderId) {
    return useForm({
        id: null,
        work_type_id: null,
        work_order_id: workOrderId,
        sub_customer_id: null,
        notes: null,
        billing_pay: 0,
        total_lumper_pay: 0,
        total_labor_split: 0,
        rate_config_hash: null,
        needs_recalculation: 0,
        recalculation_reason: null,
        is_locked: 0,
        lock_reason: null,
        created_at: null,
        updated_at: null,
        work_type: null,
        sub_customer: null,
        customer: null,
        identifiers: [],
        bracket_types: [],
        values: [],
        pay: [],
    });
}

export const useWorkOrderDialogStore = defineStore('WorkOrderDialogStore', {
    state: () => ({
        open: false,
        loaded: false,
        locked: false,
        docked: false,
        editing: false,
        minimized: false,
        loading: {
            workOrder: false,
            workItems: false,
            customer: false,
            identifierTypes: false,
            saving: false,
        },
        customer: emptyCustomer(),
        workOrder: useForm({
            id: null,
            customer_id: null,
            user_id: null,
            status: null,
            completed_at: null,
            created_at: null,
            updated_at: null,
            contact_id: null,
            work_start_at: null,
            invoice_id: null,
            contact: null,
            customer: null,
            creator: null,
            total: {
                work_items: 0,
                laborers: 0,
                gross: 0,
                expenses: 0,
                profit_percentage: 0,
            },
        }),
        workItems: {
            rows: [],
            editing: [],
            errors: { new: [], existing: [] },
            filter: {
                form: useForm({
                    page: 1,
                    rows: 25,
                    search: '',
                    sort: 'created_at',
                    direction: 'desc',
                    searchColumn: [],
                }),
                to: 0,
                from: 0,
                total: 0,
                per_page: 25,
                last_page: 1,
                current_page: 1,
                unfiltered_total: 0,
            },
        },
    }),
    getters: {
        isLoaded() {
            return this.loaded;
        },
        isDirty() {
            if (!this.loaded) return false;
            if (this.workOrder.isDirty) return true;
            return this.workItems.editing.some(item => !item.id || (item.isDirty ?? false));
        },
        identifierTypes() {
            return this.customer.identifier_types ?? [];
        },
    },
    actions: {
        async openDialog(workOrderId = null, customerId = null) {
            if (workOrderId && this.workOrder.id !== workOrderId) {
                this.loaded = false;
                this.reset();
            }
            if (workOrderId && !this.loaded) {
                await this.fetch(workOrderId, customerId);
            }
            if (!this.open) {
                this.open = true;
            } else {
                this.minimized = false;
                this.docked = false;
            }
        },
        async closeDialog() {
            this.open = false;
            this.reset();
        },
        async fetch(workOrderId = null, customerId = null, forceRefresh = false) {
            if (this.loaded && !forceRefresh) return;
            await this.getWorkOrder(workOrderId ?? this.workOrder.id, forceRefresh);
            const resolvedCustomerId = customerId ?? this.workOrder.customer_id;
            await Promise.all([
                this.getWorkItems(workOrderId ?? this.workOrder.id, forceRefresh),
                this.getCustomer(resolvedCustomerId, forceRefresh),
                this.getIdentifierTypes(resolvedCustomerId, forceRefresh),
            ]);
            this.loaded = true;
        },
        async getWorkOrder(workOrderId, forceRefresh = false) {
            if (this.loading.workOrder && !forceRefresh) return;
            this.loading.workOrder = true;
            try {
                const { data } = await axios.get(route('api.v1.work-orders.show', workOrderId));
                if (!data.success) {
                    console.error(data.message ?? 'An error occurred while fetching the work order.');
                    return;
                }
                this.workOrder = useForm(data.work_order);
            } catch (error) {
                console.error(error);
            } finally {
                this.loading.workOrder = false;
            }
        },
        async getWorkItems(workOrderId = null, forceRefresh = false) {
            if (this.loading.workItems && !forceRefresh) return;
            if ((workOrderId ?? this.workOrder.id) === null) {
                console.error('No work order ID provided.');
                return;
            }
            this.loading.workItems = true;
            try {
                const { data } = await axios.get(
                    route('api.v1.work-orders.items', workOrderId ?? this.workOrder.id),
                    { params: this.workItems.filter.form.data() }
                );
                this.workItems.rows = data.data ?? [];
                this.workItems.filter.to = data.to ?? 0;
                this.workItems.filter.from = data.from ?? 0;
                this.workItems.filter.total = data.total ?? 0;
                this.workItems.filter.per_page = data.per_page ?? this.workItems.filter.form.rows;
                this.workItems.filter.last_page = data.last_page ?? 1;
                this.workItems.filter.current_page = data.current_page ?? 1;
                this.workItems.filter.unfiltered_total = data.real_total ?? 0;
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching the work items.');
            } finally {
                this.loading.workItems = false;
            }
        },
        async getCustomer(customerId = null, forceRefresh = false) {
            if (this.loading.customer && !forceRefresh) return;
            const id = customerId ?? this.workOrder.customer_id;
            if (id === null) {
                console.error('No customer ID provided.');
                return;
            }
            this.loading.customer = true;
            try {
                const { data } = await axios.get(route('api.v1.customer.work-types', id));
                if (!data.success) {
                    console.error(data.message ?? 'An error occurred while fetching the customer.');
                    return;
                }
                this.customer = {
                    ...emptyCustomer(),
                    id,
                    ...data.data,
                    identifier_types: this.customer.identifier_types ?? [],
                    alt_contact: this.customer.alt_contact ?? [],
                };
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching the customer.');
            } finally {
                this.loading.customer = false;
            }
        },
        async getIdentifierTypes(customerId = null, forceRefresh = false) {
            const id = customerId ?? this.workOrder.customer_id;
            if (id === null) return;
            if (this.loading.identifierTypes && !forceRefresh) return;
            if (this.customer.identifier_types?.length && !forceRefresh) return;
            this.loading.identifierTypes = true;
            try {
                const { data } = await axios.get(route('api.v1.customer.identifier-types', id));
                this.customer.identifier_types = data.data ?? [];
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching identifier types.');
            } finally {
                this.loading.identifierTypes = false;
            }
        },
        async fetchAltContacts(customerId = null, forceRefresh = false) {
            const id = customerId ?? this.workOrder.customer_id;
            if (id === null) return [];
            if (this.customer.alt_contact?.length && !forceRefresh) return this.customer.alt_contact;
            try {
                const { data } = await axios.get(route('api.v1.customer.alt-locations', id));
                this.customer.alt_contact = data.data?.alt_contact ?? [];
                return this.customer.alt_contact;
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching alternate contacts.');
                return [];
            }
        },
        async fetchAssignedWorkers(customerId, params = {}) {
            if (!customerId) return { rows: [], total: 0, current_page: 1, last_page: 1 };
            try {
                const { data } = await axios.get(
                    route('api.v1.customer.assigned-workers', customerId),
                    { params }
                );
                return data.workers ?? { rows: [], total: 0, current_page: 1, last_page: 1 };
            } catch (error) {
                console.error(error ?? 'An error occurred while fetching assigned workers.');
                return { rows: [], total: 0, current_page: 1, last_page: 1 };
            }
        },
        async validateIdentifier(identifier, value, workItemId = null) {
            try {
                const { data } = await axios.get(
                    route('api.v1.identifier-types.validate.identifier'),
                    {
                        params: {
                            identifier_type_id: identifier.identifier_type_id ?? identifier.type?.id,
                            value,
                            work_item_id: workItemId,
                        },
                    }
                );
                return data;
            } catch (error) {
                return error?.response?.data ?? { success: false };
            }
        },
        async validateValues(item) {
            const workItemId = item.id ?? null;
            try {
                const url = workItemId
                    ? route('api.v1.work-items.validate', workItemId)
                    : route('api.v1.work-items.validate');
                const { data } = await axios.post(url, {
                    work_type_id: item.work_type_id,
                    sub_customer_id: item.sub_customer_id,
                    values: item.values,
                });
                return data;
            } catch (error) {
                return error?.response?.data ?? { success: false };
            }
        },
        create() {
            const template = this.workItems.editing[this.workItems.editing.length - 1]
                ?? this.workItems.rows[0]
                ?? null;
            const item = emptyWorkItem(this.workOrder.id);
            if (template) {
                item.work_type_id = template.work_type_id ?? null;
                item.sub_customer_id = template.sub_customer_id ?? null;
                item.work_type = template.work_type ?? null;
                item.sub_customer = template.sub_customer ?? null;
                item.identifiers = (template.identifiers ?? []).map(i => ({
                    identifier_type_id: i.identifier_type_id,
                    type: i.type,
                    value: '',
                }));
                item.values = (template.values ?? []).map(v => ({
                    bracket_type_id: v.bracket_type_id,
                    type: v.type,
                    value: 0,
                }));
                item.bracket_types = (template.bracket_types ?? []).slice();
            }
            this.workItems.editing.push(item);
        },
        beginEdit(row) {
            if (this.workItems.editing.some(item => item.id === row.id)) return;
            this.workItems.editing.push(useForm(JSON.parse(JSON.stringify(row))));
        },
        cancelEdit(index) {
            if (index < 0 || index >= this.workItems.editing.length) return;
            this.workItems.editing.splice(index, 1);
        },
        async saveWorkItem(index) {
            const item = this.workItems.editing[index];
            if (!item) return false;
            const isNew = !item.id;
            const url = isNew
                ? route('api.v1.work-items.store')
                : route('api.v1.work-items.update', item.id);
            const method = isNew ? 'post' : 'put';
            this.loading.saving = true;
            let succeeded = false;
            await new Promise(resolve => {
                item[method](url, {
                    preserveScroll: true,
                    onSuccess: () => {
                        succeeded = true;
                        this.workItems.editing.splice(index, 1);
                        Notify.create({ type: 'positive', message: 'Work item saved' });
                        resolve();
                    },
                    onError: (errors) => {
                        Notify.create({ type: 'negative', message: 'Work item could not be saved' });
                        this.workItems.errors[isNew ? 'new' : 'existing'] = [{ index, id: item.id, details: errors }];
                        resolve();
                    },
                    onFinish: () => {
                        this.loading.saving = false;
                    },
                });
            });
            if (succeeded) {
                await this.getWorkItems(null, true);
            }
            return succeeded;
        },
        async submitWorkOrder() {
            if (!this.workOrder.id) return false;
            if (!this.workItems.editing.length) return true;
            const payload = { work_items: this.workItems.editing.map(item => item.data()) };
            this.loading.saving = true;
            try {
                const { data } = await axios.put(
                    route('api.v1.work-orders.items.update', this.workOrder.id),
                    payload
                );
                this.workItems.editing = [];
                this.workItems.errors = { new: [], existing: [] };
                await this.getWorkItems(null, true);
                Notify.create({ type: 'positive', message: data.message ?? 'Work order saved' });
                return true;
            } catch (error) {
                const errors = error?.response?.data?.errors ?? {};
                this.workItems.errors = {
                    new: errors.newWorkItemErrors ?? [],
                    existing: errors.workItemErrors ?? [],
                };
                Notify.create({
                    type: 'negative',
                    message: error?.response?.data?.message ?? 'Work order could not be saved',
                });
                return false;
            } finally {
                this.loading.saving = false;
            }
        },
        async saveWorkOrder() {
            if (!this.workOrder.id) return false;
            let succeeded = false;
            this.loading.saving = true;
            await new Promise(resolve => {
                this.workOrder.put(route('api.v1.work-orders.update', this.workOrder.id), {
                    preserveScroll: true,
                    onSuccess: () => {
                        succeeded = true;
                        Notify.create({ type: 'positive', message: 'Work order updated' });
                        resolve();
                    },
                    onError: () => {
                        Notify.create({ type: 'negative', message: 'Work order could not be updated' });
                        resolve();
                    },
                    onFinish: () => {
                        this.loading.saving = false;
                    },
                });
            });
            return succeeded;
        },
        async patchWorkItem(row, patch) {
            if (!row?.id) return false;
            Object.assign(row, patch);
            try {
                await axios.put(route('api.v1.work-items.update', row.id), row);
                await this.getWorkItems(null, true);
                Notify.create({ type: 'positive', message: 'Work item updated' });
                return true;
            } catch (error) {
                Notify.create({
                    type: 'negative',
                    message: error?.response?.data?.message ?? 'Work item could not be updated',
                });
                return false;
            }
        },
        async destroyWorkItem(item, index = null) {
            if (index !== null && index >= 0 && index < this.workItems.editing.length && !item.id) {
                this.workItems.editing.splice(index, 1);
                return true;
            }
            if (!item.id) return false;
            try {
                await axios.delete(route('api.v1.work-orders.items.delete', {
                    workOrder: this.workOrder.id,
                    workItem: item.id,
                }));
                if (index !== null && index >= 0 && index < this.workItems.editing.length) {
                    this.workItems.editing.splice(index, 1);
                }
                await this.getWorkItems(null, true);
                Notify.create({ type: 'positive', message: 'Work item deleted' });
                return true;
            } catch (error) {
                Notify.create({
                    type: 'negative',
                    message: error?.response?.data?.message ?? 'Work item could not be deleted',
                });
                return false;
            }
        },
        async deleteWorkOrder() {
            if (!this.workOrder.id) return false;
            let succeeded = false;
            await new Promise(resolve => {
                this.workOrder.delete(route('api.v1.work-orders.destroy', this.workOrder.id), {
                    preserveScroll: true,
                    onSuccess: () => {
                        succeeded = true;
                        Notify.create({ type: 'positive', message: 'Work order deleted' });
                        this.closeDialog();
                        resolve();
                    },
                    onError: () => {
                        Notify.create({ type: 'negative', message: 'Work order could not be deleted' });
                        resolve();
                    },
                });
            });
            return succeeded;
        },
        async updateWorkOrderContact(contact) {
            this.workOrder.contact_id = contact?.id ?? null;
            this.workOrder.contact = contact ?? null;
            return this.saveWorkOrder();
        },
        async updateWorkOrderDate(date) {
            this.workOrder.work_start_at = date;
            return this.saveWorkOrder();
        },
        reset() {
            this.loaded = false;
            this.loading.workOrder = false;
            this.loading.workItems = false;
            this.loading.customer = false;
            this.loading.identifierTypes = false;
            this.loading.saving = false;
            this.workOrder.reset();
            this.workItems.rows = [];
            this.workItems.editing = [];
            this.workItems.errors = { new: [], existing: [] };
            this.workItems.filter.form.reset();
            this.workItems.filter.to = 0;
            this.workItems.filter.from = 0;
            this.workItems.filter.total = 0;
            this.workItems.filter.per_page = 25;
            this.workItems.filter.last_page = 1;
            this.workItems.filter.current_page = 1;
            this.workItems.filter.unfiltered_total = 0;
            this.customer = emptyCustomer();
        },
    },
});
